import { useMemo, useState, type ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Tabs } from '@mantine/core';
import { ListTree, MessagesSquare, UsersRound } from 'lucide-react';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import SurfaceBreadcrumbRow from '@/shared/surfaces/SurfaceBreadcrumbRow';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { useAssistFilters } from '@/shared/surfaces/assist/AssistSurfaceProvider';
import { useSurface } from '@/shared/surfaces/useSurface';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import PersonActivityGrid from '@/shared/surfaces/PersonActivityGrid';
import type { AnalyticsSurface } from '@/shared/surfaces/AnalyticsSurface';
import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import { NULL_DIMENSION_BUCKET } from '@/shared/analytics/AnalyticsTypes';
import UseCasePromptsTab from './UseCasePromptsTab';
const TAB_STORAGE_KEY = 'ut-usecase-subtab';
const TAB_VALUES = ['people', 'prompts'] as const;

function storedTab(): string {
    if (typeof window === 'undefined') return 'people';
    const value = window.localStorage.getItem(TAB_STORAGE_KEY);
    return value !== null && (TAB_VALUES as readonly string[]).includes(value) ? value : 'people';
}

/** One specific task within a use case: its people and prompts as tabs — the last-used tab is remembered as the user's default entry point. */
export default function UseCaseSubcategoryPage(): ReactElement {
    const surface = useSurface('behavior');
    if (surface === undefined) return <TopicNotFound />;
    return (
        <SurfaceAssistScope surface={surface}>
            <UseCaseSubcategoryBody surface={surface} />
        </SurfaceAssistScope>
    );
}

function UseCaseSubcategoryBody({ surface }: { surface: AnalyticsSurface }): ReactElement {
    const { category, subcategory } = useParams<{ category: string; subcategory: string }>();
    const ambientFilters = useAssistFilters();
    const navigate = useNavigate();
    const [tab, setTabState] = useState<string | null>(storedTab);

    const categoryValue = category ?? '';
    const subcategoryValue = subcategory ?? '';
    const categoryLabel = categoryValue === NULL_DIMENSION_BUCKET ? 'Unclassified' : categoryValue;
    const subcategoryLabel = subcategoryValue === NULL_DIMENSION_BUCKET ? 'Unclassified' : subcategoryValue;
    const filters = useMemo<AnalyticsFilter[]>(
        () => [{ field: 'task_category', values: [categoryValue] }, { field: 'task_subcategory', values: [subcategoryValue] }, ...ambientFilters],
        [categoryValue, subcategoryValue, ambientFilters],
    );

    if (categoryValue.length === 0 || subcategoryValue.length === 0) return <TopicNotFound />;

    const setTab = (next: string | null): void => {
        setTabState(next);
        if (next !== null) window.localStorage.setItem(TAB_STORAGE_KEY, next);
    };

    const basePath = `${surface.basePath}/use-cases`;
    const subcategoryPath = `${basePath}/${encodeURIComponent(categoryValue)}/sub/${encodeURIComponent(subcategoryValue)}`;

    return (
        <Box pb="xl">
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            <SurfaceBreadcrumbRow
                items={[{ label: 'Use Cases', to: basePath }, { label: categoryLabel, to: `${basePath}/${encodeURIComponent(categoryValue)}` }, { label: subcategoryLabel }]}
            />

            <PageHeaderComponent
                icon={ListTree}
                eyebrow={`${categoryLabel} · specific task`}
                title={subcategoryLabel}
                subtitle="A closer look at one task — who runs it, and the prompts they bring."
            />

            <Tabs value={tab} onChange={setTab} keepMounted={false}>
                <Tabs.List mb="lg">
                    <Tabs.Tab value="people" leftSection={<UsersRound size={14} />}>
                        Users
                    </Tabs.Tab>
                    <Tabs.Tab value="prompts" leftSection={<MessagesSquare size={14} />}>
                        Prompts
                    </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="people">
                    {tab === 'people' && (
                        <PersonActivityGrid filters={filters} onRowClick={(userId) => navigate(`${subcategoryPath}/${encodeURIComponent(userId)}`)} persistenceKey="use-case-sub-people" />
                    )}
                </Tabs.Panel>

                <Tabs.Panel value="prompts">{tab === 'prompts' && <UseCasePromptsTab filters={filters} />}</Tabs.Panel>
            </Tabs>
        </Box>
    );
}
